import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { it } from '../../services/api';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import { SearchableSelect } from '../../components/common/SearchableSelect';
import { theme } from '../../theme';
import { useAuth } from '../../contexts/AuthContext';

function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'it_admin' || user?.role === 'super_admin';

  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Admin update form
  const [updateForm, setUpdateForm] = useState({ status: '', assigned_to: '', priority: '', resolution_notes: '' });
  const [updating, setUpdating] = useState(false);
  const [employees, setEmployees] = useState([]);

  const load = useCallback(async () => {
    try {
      const [tr, cr] = await Promise.allSettled([it.getTicket(id), it.getTicketComments(id)]);
      if (tr.status === 'fulfilled') {
        const t = tr.value.data;
        setTicket(t);
        setUpdateForm({
          status: t.status || '',
          assigned_to: t.assigned_to ? String(t.assigned_to) : '',
          priority: t.priority || '',
          resolution_notes: t.resolution_notes || '',
        });
      }
      if (cr.status === 'fulfilled') setComments(cr.value.data || []);
    } catch { } finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    load();
    if (isAdmin) {
      it.getITStaff().then((r) => setEmployees(r.data || [])).catch(err => console.warn('[IT]', err?.message));
    }
  }, [load, isAdmin]);

  const handleComment = async () => {
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await it.addTicketComment(id, comment.trim());
      setComment('');
      load();
    } catch { alert('Failed to add comment.'); } finally { setSubmitting(false); }
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const payload = {};
      if (updateForm.status) payload.status = updateForm.status;
      if (updateForm.priority) payload.priority = updateForm.priority;
      if (updateForm.assigned_to !== '') payload.assigned_to = updateForm.assigned_to ? Number(updateForm.assigned_to) : null;
      if (updateForm.resolution_notes !== (ticket?.resolution_notes || '')) payload.resolution_notes = updateForm.resolution_notes;
      await it.updateTicket(id, payload);
      load();
    } catch { alert('Failed to update ticket.'); } finally { setUpdating(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}>Loading...</div>;
  if (!ticket) return <p>Ticket not found.</p>;

  const needsResolutionNotes = updateForm.status === 'resolved' || updateForm.status === 'closed';

  return (
    <div style={{ maxWidth: '900px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <code style={{ background: theme.pageBg, padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.8rem', border: `1px solid ${theme.border}` }}>
              {ticket.ticket_code || `#${ticket.id}`}
            </code>
            <Badge status={ticket.status || 'open'} />
            <Badge status={ticket.priority || 'medium'} />
          </div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: theme.text, margin: 0 }}>{ticket.title}</h1>
        </div>
        <Button variant="outlineGray" onClick={() => navigate(-1)}>← Back</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.5rem' }}>
        {/* Main content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <Card title="Description">
            <p style={{ margin: 0, color: theme.gray, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
            {ticket.resolution_notes && (
              <div style={{ marginTop: '1rem', padding: '0.875rem', background: '#F0FDF4', borderRadius: '0.5rem', border: '1px solid #BBF7D0' }}>
                <p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#065F46', margin: '0 0 0.5rem' }}>Resolution Notes:</p>
                <p style={{ margin: 0, color: '#065F46', fontSize: '0.875rem' }}>{ticket.resolution_notes}</p>
              </div>
            )}
          </Card>

          {/* Comments */}
          <Card title={`Comments (${comments.length})`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
              {comments.length === 0 ? (
                <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1rem 0' }}>No comments yet</p>
              ) : comments.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.75rem' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: theme.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem', color: theme.primaryDark, flexShrink: 0 }}>
                    {(c.author_name || c.author?.full_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.3rem' }}>
                      <strong style={{ fontSize: '0.85rem' }}>{c.author_name || c.author?.full_name || 'Unknown'}</strong>
                      <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: theme.gray, lineHeight: 1.5, background: theme.pageBg, padding: '0.625rem 0.875rem', borderRadius: '0.5rem', border: `1px solid ${theme.border}` }}>
                      {c.comment || c.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." rows={3}
                style={{ width: '100%', padding: '0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: '0.75rem' }}
                onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                onBlur={(e) => (e.target.style.borderColor = theme.border)}
              />
              <Button variant="primary" size="sm" loading={submitting} onClick={handleComment} disabled={!comment.trim()}>Add Comment</Button>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Card title="Ticket Details">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.85rem' }}>
              {[
                { label: 'Category', value: ticket.category },
                { label: 'Priority', value: <Badge status={ticket.priority} /> },
                { label: 'Status', value: <Badge status={ticket.status} /> },
                { label: 'Raised By', value: ticket.raised_by_name },
                { label: 'Assigned To', value: ticket.assignee_name || 'Unassigned' },
                { label: 'Asset', value: ticket.asset_name || (ticket.asset_id ? `Asset #${ticket.asset_id}` : null) },
                { label: 'Created', value: ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : null },
                { label: 'Resolved', value: ticket.resolved_at ? new Date(ticket.resolved_at).toLocaleDateString() : null },
                { label: 'Resolved By', value: ticket.resolved_by_name || null },
              ].map(({ label, value }, i) => (
                value ? (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px solid ${theme.border}`, paddingBottom: '0.625rem' }}>
                    <span style={{ color: theme.textMuted, fontWeight: 500 }}>{label}</span>
                    <span style={{ color: theme.text, fontWeight: 500, textAlign: 'right', maxWidth: '160px' }}>{value}</span>
                  </div>
                ) : null
              ))}
            </div>
          </Card>

          {isAdmin && (
            <Card title="Update Ticket">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {/* Status */}
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.textMuted, display: 'block', marginBottom: '0.3rem' }}>Status</label>
                  <select value={updateForm.status} onChange={(e) => setUpdateForm((f) => ({ ...f, status: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.85rem', color: theme.text, outline: 'none', boxSizing: 'border-box' }}>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                    <option value="escalated">Escalated</option>
                  </select>
                </div>

                {/* Priority */}
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.textMuted, display: 'block', marginBottom: '0.3rem' }}>Priority</label>
                  <select value={updateForm.priority} onChange={(e) => setUpdateForm((f) => ({ ...f, priority: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.85rem', color: theme.text, outline: 'none', boxSizing: 'border-box' }}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                {/* Assign to IT staff */}
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.textMuted, display: 'block', marginBottom: '0.3rem' }}>Assign To</label>
                  <SearchableSelect
                    options={employees.map((e) => ({
                      value: String(e.id),
                      label: `${e.full_name}${e.role === 'it_support' ? ' (Support)' : ' (Admin)'}`,
                    }))}
                    value={updateForm.assigned_to}
                    onChange={(val) => setUpdateForm((f) => ({ ...f, assigned_to: val || '' }))}
                    placeholder="Unassigned"
                  />
                </div>

                {/* Resolution notes — shown when resolving or closing */}
                {needsResolutionNotes && (
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.textMuted, display: 'block', marginBottom: '0.3rem' }}>Resolution Notes *</label>
                    <textarea value={updateForm.resolution_notes} onChange={(e) => setUpdateForm((f) => ({ ...f, resolution_notes: e.target.value }))}
                      placeholder="Describe how the issue was resolved..."
                      style={{ width: '100%', padding: '0.5rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.85rem', color: theme.text, outline: 'none', resize: 'vertical', minHeight: '80px', boxSizing: 'border-box' }} />
                  </div>
                )}

                <Button variant="primary" size="sm" fullWidth loading={updating} onClick={handleUpdate}>Save Changes</Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default TicketDetail;
